import { motion } from "framer-motion";
import { 
  Building2, 
  Landmark, 
  Home, 
  Users2, 
  CircleDollarSign, 
  TrendingUp, 
  FileCheck, 
  Shield,
  ChevronRight,
  CheckCircle2,
  AlertCircle
} from "lucide-react";

export default function RegulatoryServices() {
  const services = [
    {
      id: 1,
      title: "Banking & Cooperative Banking Advisory",
      icon: Landmark,
      image: "https://images.unsplash.com/photo-1541354329998-f4d9a9f9297f?w=800&q=80",
      services: [
        "Banking license and regulatory approval advisory",
        "Cooperative bank registration, conversion, and restructuring",
        "Capital structuring, governance, and compliance advisory",
        "Ongoing RBI / NABARD / RCS regulatory support"
      ]
    },
    {
      id: 2,
      title: "NBFC Registration & Advisory",
      icon: Building2,
      image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80",
      services: [
        "NBFC registration and classification advisory",
        "Licensing documentation, NOF, and capital adequacy support",
        "RBI compliance, governance, and risk frameworks",
        "M&A, restructuring, and change-in-control advisory"
      ]
    },
    {
      id: 3,
      title: "Housing Finance Company (HFC) Advisory",
      icon: Home,
      image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80",
      services: [
        "HFC registration and RBI licensing support",
        "Capital adequacy, prudential norms, and product structuring",
        "Compliance, ALM, and risk management advisory"
      ]
    },
    {
      id: 4,
      title: "Microfinance & NBFC-MFI Advisory",
      icon: Users2,
      image: "https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=800&q=80",
      services: [
        "NBFC-MFI registration and regulatory structuring",
        "Microfinance compliance, Fair Practices Code advisory",
        "Portfolio quality, recovery, and scale-up advisory"
      ]
    },
    {
      id: 5,
      title: "Nidhi Company Advisory",
      icon: CircleDollarSign,
      image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80",
      services: [
        "Nidhi company formation and registration",
        "MCA filings and statutory compliance",
        "Deposit norms, member compliance, and reporting support"
      ]
    },
    {
      id: 6,
      title: "Alternative Investment Fund (AIF) Advisory",
      icon: TrendingUp,
      image: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&q=80",
      services: [
        "SEBI registration for Category I, II & III AIFs",
        "Fund structuring, PPM preparation, and sponsor/manager setup",
        "Ongoing SEBI compliance and operational advisory"
      ]
    },
    {
      id: 7,
      title: "NCD & Debt Capital Market Advisory",
      icon: FileCheck,
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80",
      services: [
        "NCD structuring, private placement, and public issue advisory",
        "Documentation, credit rating, trustee, and listing coordination",
        "Post-issuance compliance and restructuring support"
      ]
    },
    {
      id: 8,
      title: "End-to-End Regulatory & Compliance Support",
      icon: Shield,
      image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80",
      services: [
        "Regulatory liaison and approval management",
        "Policy drafting, inspections, and audit advisory",
        "Representation support for regulatory queries and notices"
      ]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-yellow-50 via-white to-yellow-50 border-b-2 border-yellow-200">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-yellow-100 rounded-full">
                <Shield className="w-16 h-16 text-yellow-600" />
              </div>
            </div>
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-black mb-6 leading-tight">
              Registration, Licensing & <br />
              <span className="text-yellow-600">Regulatory Advisory Services</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto mb-4">
              Banks, Financial Institutions & Capital Market Entities
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 mt-8">
              <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-md">
                <CheckCircle2 className="w-5 h-5 text-yellow-600" />
                <span className="text-gray-700 font-medium">RBI Approved</span>
              </div>
              <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-md">
                <CheckCircle2 className="w-5 h-5 text-yellow-600" />
                <span className="text-gray-700 font-medium">SEBI Compliant</span>
              </div>
              <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-full shadow-md">
                <CheckCircle2 className="w-5 h-5 text-yellow-600" />
                <span className="text-gray-700 font-medium">Expert Advisory</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        variants={containerVariants}
        className="max-w-7xl mx-auto px-6 py-20"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
          {services.map((service) => {
            const IconComponent = service.icon;
            return (
              <motion.div
                key={service.id}
                variants={itemVariants}
                className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group border-2 border-gray-100 hover:border-yellow-400"
              >
                {/* Image Section with Overlay */}
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                  
                  {/* Icon Badge */}
                  <div className="absolute top-4 right-4">
                    <div className="p-3 bg-yellow-500 rounded-xl shadow-lg">
                      <IconComponent className="w-7 h-7 text-white" />
                    </div>
                  </div>
                  
                  {/* Title on Image */}
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-2xl font-bold text-white leading-tight">
                      {service.title}
                    </h3>
                  </div>
                </div>

                {/* Content Section */}
                <div className="p-6">
                  <ul className="space-y-3">
                    {service.services.map((item, idx) => (
                      <li key={idx} className="flex items-start space-x-3 group/item">
                        <ChevronRight className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0 group-hover/item:translate-x-1 transition-transform" />
                        <span className="text-gray-700 text-base leading-relaxed">
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Bottom Accent */}
                <div className="px-6 pb-6">
                  <div className="h-1 bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 rounded-full transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </motion.section>

      {/* Disclaimer Section */}
      <motion.section
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="max-w-7xl mx-auto px-6 pb-20"
      >
        <div className="bg-gradient-to-br from-yellow-50 to-white border-2 border-yellow-200 rounded-2xl p-8 md:p-12">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="p-3 bg-yellow-100 rounded-xl">
                <AlertCircle className="w-8 h-8 text-yellow-600" />
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-black mb-4">Disclaimer</h3>
              <p className="text-gray-700 text-lg leading-relaxed">
                All registration, licensing, and regulatory advisory services are provided in accordance with applicable laws and regulations. Final approvals remain subject to the discretion of the respective regulatory authorities.
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-500 text-white py-20"
      >
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Navigate Regulatory Compliance?
          </h2>
          <p className="text-xl md:text-2xl mb-10 text-yellow-50">
            Our regulatory experts provide end-to-end support for licensing, registration, and ongoing compliance
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-10 py-4 bg-white text-yellow-600 font-bold text-lg rounded-xl hover:bg-gray-100 transform hover:scale-105 transition-all duration-300 shadow-2xl">
              Schedule Consultation
            </button>
            <button className="px-10 py-4 bg-transparent border-2 border-white text-white font-bold text-lg rounded-xl hover:bg-white hover:text-yellow-600 transform hover:scale-105 transition-all duration-300">
              Download Brochure
            </button>
          </div>
        </div>
      </motion.section>
    </div>
  );
}
